import { of, Observable } from 'rxjs'
import { switchMap, map, catchError, tap, mapTo, take, filter } from 'rxjs/operators'

import { Injectable } from '@angular/core'
import { Actions, ofType, createEffect } from '@ngrx/effects'
import { Router } from '@angular/router'

import { AuthService } from 'src/app/common'
import * as CenterActions from '../actions/center.actions'
import * as UserActions from '../actions/user.actions'
import { JrpcService } from 'src/app/common/jrpc.service'
import { CenterStatus } from '../reducers/flight-network.reducer'
import { Store } from '@ngrx/store'
import { AppState, getFlightNetworkCenterById } from '..'
import { ErrorHandlerService } from 'src/app/common/error-handler.service'

@Injectable()
export class CenterEffects {
  statusesToPass: Set<CenterStatus> = new Set([CenterStatus.Open, CenterStatus.Closing, CenterStatus.CanBeClosed])

  initOpenStateChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CenterActions.initOpenStateChange),
      switchMap(({ status, center, requiredChecklists }) => {
        let obs: Observable<any>
        if (center.open_state !== status) {
          obs = this.jrpc.setCenterState(status, requiredChecklists)
        } else {
          obs = of(null)
        }
        return obs.pipe(
          map(() => CenterActions.initOpenStateChangeSuccess()),
          catchError((errorResponse) => {
            const message = 'Some server error'
            this.errorHandlerService.showErrorMessage(errorResponse, message)
            return of(CenterActions.initOpenStateChangeFailure({ error: errorResponse }))
          }),
        )
      }),
    ),
  )

  changeOpenState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CenterActions.changeOpenState),
      switchMap(({ open_state, center_id }) =>
        this.store.select(getFlightNetworkCenterById, { id: center_id }).pipe(
          take(1),
          filter((center) => Boolean(center)),
          switchMap((center) => {
            const id = this.getLocalStorageCenterId()

            if (!center || open_state === CenterStatus.Closed || center.id !== id) {
              const prevState = center.open_state
              return of(CenterActions.changeOpenStateSuccess({ center: null, open_state: CenterStatus.Closed })).pipe(
                tap(() => {
                  localStorage.removeItem(this.storageKey)
                  const url = this.router.url
                  if (
                    prevState === CenterStatus.CanBeClosed &&
                    open_state === CenterStatus.Closed &&
                    (url.match('/dashboard') || url.match('/package-queue'))
                  ) {
                    this.router.navigateByUrl('/app/welcome')
                  }
                }),
              )
            } else {
              const prevState = center.open_state
              center.open_state = open_state
              return of(CenterActions.changeOpenStateSuccess({ center, open_state })).pipe(
                tap(() => {
                  if (prevState === CenterStatus.Closed && open_state === CenterStatus.Open) {
                    this.router.navigateByUrl('/app/dashboard')
                  }
                }),
              )
            }
          }),
          catchError((errorResponse) => {
            const message = 'Some server error'
            this.errorHandlerService.showErrorMessage(errorResponse, message)
            return of(CenterActions.changeOpenStateFailure({ error: errorResponse }))
          }),
        ),
      ),
    ),
  )

  setCenter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CenterActions.setCenter),
      map(({ center }) => {
        const id = this.getLocalStorageCenterId()
        return CenterActions.setCenterSuccess({ center: center?.id === id ? center : null })
      }),
    ),
  )

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.logoutSuccess),
      tap(() => localStorage.removeItem(this.storageKey)),
      mapTo(CenterActions.setCenter({ center: null })),
    ),
  )

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.authenticateSuccess),
      switchMap(() => {
        const id = this.getLocalStorageCenterId()

        if (!isNaN(id)) {
          return this.authService.getCurrentCenter().pipe(
            map((center) => CenterActions.loadCenterSuccess({ center })),
            catchError((error) => of(CenterActions.loadCenterFailure({ error }))),
          )
        }
        return of(CenterActions.setCenter({ center: null }))
      }),
    ),
  )

  authFailed$ = createEffect(() =>
    this.actions$.pipe(ofType(UserActions.authenticateFailure), mapTo(CenterActions.setCenter({ center: null }))),
  )

  localStorageUpdate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CenterActions.setCenterIdInLocalStorage),
        tap(({ id }) => {
          localStorage.setItem(this.storageKey, String(id))
        }),
      ),
    {
      dispatch: false,
    },
  )

  redirectAfterOpen$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CenterActions.setCenterIdInLocalStorage),
        tap(({ id }) => {
          localStorage.setItem(this.storageKey, String(id))
        }),
      ),
    {
      dispatch: false,
    },
  )

  private storageKey = 'openedCenterId'

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private router: Router,
    private jrpc: JrpcService,
    private store: Store<AppState>,
    private errorHandlerService: ErrorHandlerService,
  ) {}

  private getLocalStorageCenterId(): number | undefined {
    const id = localStorage.getItem(this.storageKey)
    if (id === null) {
      return undefined
    }
    return Number(localStorage.getItem(this.storageKey))
  }
}
